import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { gisPlatformApi } from '../../services/api';
import Modal from '../../components/common/Modal';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';
import Alert from '../../components/common/Alert';
import { useAuthStore } from '../../store/authStore';
import {
  Layers, Thermometer, FileText, Navigation2,
  Upload as UploadIcon, CheckCircle2, AlertCircle, Activity,
  Info, ChevronDown, ChevronUp, X,
} from 'lucide-react';

interface Mission {
  id: string;
  name: string;
  description?: string;
  status: 'planned' | 'in_progress' | 'completed' | 'failed';
  flight_date?: string;
  area_hectares?: number;
  crop_type?: string;
  created_at: string;
  updated_at: string;
  drone_serial_number?: string;
  sensor_calibration_date?: string;
  pilot_id?: string;
  flight_log_path?: string;
  telemetry_correlation_status?: 'pending' | 'correlated' | 'failed';
  location?: {
    type: 'Point';
    coordinates: [number, number];
  };
  metadata?: {
    integrity_seal?: string;
    ingestion_timestamp?: string;
    file_count?: number;
    termination_comment?: string;
    terminated_at?: string;
    terminated_by?: string;
    completed_at?: string;
    completed_by?: string;
    pilot_notes?: string;
    [key: string]: any;
  };
}

interface MissionFile {
  id: string;
  data_type: string;
  file_path: string;
  file_size: number;
  created_at: string;
  metadata: {
    file_name?: string;
    mime_type?: string;
    upload_status?: string;
    public_url?: string;
    sha256?: string;
    [key: string]: any;
  };
}

interface AiAnalysis {
  id: string;
  analysis_type: string;
  confidence?: number;
  metadata?: {
    issue_classification?: string;
    severity_level?: string;
    affected_area_ha?: number;
    ndvi_affected?: number;
    ndvi_healthy?: number;
    satellite_drone_match?: boolean;
    recommended_actions?: any[];
    model_used?: string;
    narrative?: string;
    [key: string]: any;
  };
  created_at: string;
}

// ─── File-type upload guidance ────────────────────────────────────────────────

interface FileTypeSpec {
  icon: React.ElementType;
  iconColour: string;
  label: string;
  formats: string;
  size: string;
  required: boolean;
  description: string;
}

const FILE_TYPES: FileTypeSpec[] = [
  {
    icon: Layers,
    iconColour: 'text-violet-600 dark:text-violet-400',
    label: 'Multispectral Imagery',
    formats: 'TIFF with band metadata',
    size: '100 MB – 500 MB',
    required: true,
    description:
      'Five-band capture (Blue 475 nm / Green 560 nm / Red 668 nm / Red Edge 717 nm / NIR 842 nm). ' +
      'Primary input for NDVI, NDRE, and NDWI computation. Required for any vegetation or crop-stress diagnosis.',
  },
  {
    icon: UploadIcon,
    iconColour: 'text-blue-600 dark:text-blue-400',
    label: 'RGB Orthomosaic',
    formats: 'TIFF / JPG',
    size: '200 MB – 1 GB',
    required: true,
    description:
      'High-resolution true-colour mosaic from the RGB camera. Used to visually confirm canopy texture, ' +
      'bare-soil patches, physical damage, and pest density. Supports photogrammetric surface models.',
  },
  {
    icon: Thermometer,
    iconColour: 'text-orange-500 dark:text-orange-400',
    label: 'Thermal Imagery',
    formats: 'TIFF / RJPEG',
    size: '20 MB – 80 MB',
    required: false,
    description:
      'Radiometric JPEG or thermal TIFF capturing surface temperature. Identifies waterlogging, root-stress ' +
      '(stomatal disruption causes canopy heat elevation), and irrigation failure zones.',
  },
  {
    icon: FileText,
    iconColour: 'text-green-600 dark:text-green-400',
    label: 'LiDAR Point Cloud',
    formats: 'LAS / LAZ',
    size: '500 MB – 5 GB',
    required: false,
    description:
      'Dense 3-D point cloud for canopy height, canopy gap fraction, and under-canopy structure. ' +
      'Required for forestry, orchard, and plantation vertical-structure assessments.',
  },
  {
    icon: Navigation2,
    iconColour: 'text-teal-600 dark:text-teal-400',
    label: 'Flight Log',
    formats: 'CSV / KML / DJI .DAT',
    size: '1 MB – 10 MB',
    required: true,
    description:
      'Full telemetry log exported from the drone (GPS path, altitude, speed, wind, battery %). ' +
      'Required for integrity verification, area-coverage confirmation, and audit chain.',
  },
];

const SCENARIOS = [
  {
    anomalyType: 'Vegetation Decline',
    colour: 'bg-red-100 dark:bg-red-900/30',
    textColour: 'text-red-700 dark:text-red-300',
    files: ['Multispectral Imagery', 'RGB Orthomosaic', 'Thermal Imagery', 'Flight Log'],
    payloadNote: 'MicaSense RedEdge-P (5-band) + FLIR Vue Pro R. Altitude 90–100 m.',
  },
  {
    anomalyType: 'Water Stress / NDWI Anomaly',
    colour: 'bg-blue-100 dark:bg-blue-900/30',
    textColour: 'text-blue-700 dark:text-blue-300',
    files: ['Multispectral Imagery', 'Thermal Imagery', 'Flight Log'],
    payloadNote: 'Multispectral + Thermal at 80–100 m. NDWI maps moisture; thermal pinpoints waterlogging.',
  },
  {
    anomalyType: 'Pest / Infestation Indicator',
    colour: 'bg-amber-100 dark:bg-amber-900/30',
    textColour: 'text-amber-700 dark:text-amber-300',
    files: ['Multispectral Imagery', 'RGB Orthomosaic', 'Flight Log'],
    payloadNote: 'High-res multispectral at 60–80 m. Reduced GSD (~3 cm) for canopy texture classification.',
  },
  {
    anomalyType: 'Bare Soil / Erosion',
    colour: 'bg-yellow-100 dark:bg-yellow-900/30',
    textColour: 'text-yellow-700 dark:text-yellow-300',
    files: ['RGB Orthomosaic', 'Multispectral Imagery', 'Flight Log'],
    payloadNote: 'RGB photogrammetric survey + multispectral to confirm NDVI depression. Altitude 80–100 m.',
  },
  {
    anomalyType: 'Canopy Height / Structure',
    colour: 'bg-emerald-100 dark:bg-emerald-900/30',
    textColour: 'text-emerald-700 dark:text-emerald-300',
    files: ['LiDAR Point Cloud', 'RGB Orthomosaic', 'Flight Log'],
    payloadNote: 'LiDAR (Zenmuse L1 or equivalent) + RGB. Orchard/plantation vertical structure analysis.',
  },
  {
    anomalyType: 'General Crop Health Screening',
    colour: 'bg-purple-100 dark:bg-purple-900/30',
    textColour: 'text-purple-700 dark:text-purple-300',
    files: ['Multispectral Imagery', 'Flight Log'],
    payloadNote: 'Standard multispectral at 100–120 m for large-area NDVI vigour screening.',
  },
];

function FileTypeRow({ spec, highlighted }: { spec: FileTypeSpec; highlighted: boolean }) {
  const [expanded, setExpanded] = useState(false);
  const Icon = spec.icon;
  return (
    <div className={`rounded-lg border transition-colors ${
      highlighted
        ? 'border-violet-300 bg-violet-50 dark:border-violet-600/50 dark:bg-violet-900/20'
        : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800/50'
    }`}>
      <button type="button" onClick={() => setExpanded(v => !v)} className="w-full flex items-center gap-3 p-3 text-left">
        <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${
          highlighted ? 'bg-violet-100 dark:bg-violet-800/40' : 'bg-gray-100 dark:bg-gray-700'
        }`}>
          <Icon className={`w-4 h-4 ${spec.iconColour}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-gray-900 dark:text-white">{spec.label}</span>
            {spec.required ? (
              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300">Required</span>
            ) : (
              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400">Conditional</span>
            )}
            {highlighted && (
              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300 flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" /> Needed for this scenario
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {spec.formats}<span className="mx-1.5 text-gray-300 dark:text-gray-600">·</span>{spec.size}
          </p>
        </div>
        <div className="flex-shrink-0 text-gray-400">
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </div>
      </button>
      {expanded && (
        <div className="px-3 pb-3 pt-0 ml-11">
          <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed">{spec.description}</p>
        </div>
      )}
    </div>
  );
}

function UploadGuidancePanel({ onClose }: { onClose: () => void }) {
  const [activeScenario, setActiveScenario] = useState<string | null>(null);
  const highlighted = activeScenario
    ? (SCENARIOS.find(s => s.anomalyType === activeScenario)?.files ?? [])
    : [];
  return (
    <div className="mb-4 rounded-lg border border-blue-200 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20 p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Info className="w-4 h-4 text-blue-600 dark:text-blue-400" />
          <span className="text-sm font-semibold text-blue-900 dark:text-blue-100">Upload Guidance — File Types &amp; Scenarios</span>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-1 rounded hover:bg-blue-100 dark:hover:bg-blue-800/40 text-blue-500 dark:text-blue-400"
          aria-label="Close upload guidance"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <div>
        <p className="text-xs font-medium text-blue-800 dark:text-blue-200 mb-2">
          Select a scenario to highlight the required files:
        </p>
        <div className="flex flex-wrap gap-2">
          {SCENARIOS.map(s => (
            <button
              key={s.anomalyType}
              type="button"
              onClick={() => setActiveScenario(prev => prev === s.anomalyType ? null : s.anomalyType)}
              className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                activeScenario === s.anomalyType
                  ? `${s.colour} ${s.textColour} border-current ring-1 ring-current`
                  : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:border-gray-400'
              }`}
            >
              {s.anomalyType}
            </button>
          ))}
        </div>
        {activeScenario && (() => {
          const sc = SCENARIOS.find(s => s.anomalyType === activeScenario)!;
          return (
            <p className="mt-2 text-xs text-gray-600 dark:text-gray-300 italic">{sc.payloadNote}</p>
          );
        })()}
      </div>
      <div className="space-y-2">
        {FILE_TYPES.map(spec => (
          <FileTypeRow
            key={spec.label}
            spec={spec}
            highlighted={highlighted.includes(spec.label)}
          />
        ))}
      </div>
    </div>
  );
}

// ── Data-type display helpers ─────────────────────────────────────────────────

const DATA_TYPE_META: Record<string, { label: string; icon: React.ElementType; colour: string }> = {
  image_multispectral: { label: 'Multispectral',  icon: Layers,       colour: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300' },
  image_thermal:       { label: 'Thermal',        icon: Thermometer,  colour: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' },
  image_rgb:           { label: 'RGB Ortho',      icon: UploadIcon,   colour: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
  lidar:               { label: 'LiDAR',          icon: FileText,     colour: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' },
  flight_log:          { label: 'Flight Log',     icon: Navigation2,  colour: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300' },
  image:               { label: 'File',           icon: UploadIcon,   colour: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300' },
};

function FileTypeBadge({ dataType }: { dataType: string }) {
  const meta = DATA_TYPE_META[dataType] ?? DATA_TYPE_META['image'];
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold ${meta.colour}`}>
      <Icon className="w-3 h-3" />
      {meta.label}
    </span>
  );
}

const ANALYSIS_TYPE_LABEL: Record<string, string> = {
  health:  'Health Assessment',
  pest:    'Pest Detection',
  disease: 'Disease Analysis',
  general: 'General',
  nlq:     'NLQ Query',
};

interface MissionStats {
  images: number;
  analyses: number;
  totalSize: number;
  totalSizeFormatted: string;
}

export const MissionDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const [mission, setMission] = useState<Mission | null>(null);
  const [files, setFiles] = useState<MissionFile[]>([]);
  const [analysis, setAnalysis] = useState<AiAnalysis[]>([]);
  const [stats, setStats] = useState<MissionStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'files' | 'analysis'>('overview');

  // Edit modal
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', description: '' });
  const [isSaving, setIsSaving] = useState(false);

  // Pilot notes
  const [pilotNotesText, setPilotNotesText] = useState('');
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const [notesError, setNotesError] = useState<string | null>(null);
  const [notesSuccess, setNotesSuccess] = useState<string | null>(null);

  // Complete mission
  const [isCompleting, setIsCompleting] = useState(false);

  // Terminate mission modal
  const [isTerminateModalOpen, setIsTerminateModalOpen] = useState(false);
  const [terminationComment, setTerminationComment] = useState('');
  const [isTerminating, setIsTerminating] = useState(false);
  const [terminateError, setTerminateError] = useState<string | null>(null);

  // File upload (inline on Files tab)
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showUploadGuidance, setShowUploadGuidance] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);
  const [isOpeningGis, setIsOpeningGis] = useState(false);

  useEffect(() => {
    if (id) loadMissionData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadMissionData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [missionRes, filesRes, analysisRes, statsRes] = await Promise.all([
        api.get(`/missions/${id}`),
        api.get(`/missions/${id}/images`),
        api.get(`/missions/${id}/analysis`).catch(() => ({ data: { data: { analysis: [] } } })),
        api.get(`/missions/${id}/stats`),
      ]);

      const m = missionRes.data.data.mission;
      setMission(m);
      setPilotNotesText(m?.metadata?.pilot_notes ?? '');
      setFiles(filesRes.data.data.images || []);
      setAnalysis(analysisRes.data.data.analysis || []);
      setStats(statsRes.data.data.stats);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load mission details');
    } finally {
      setLoading(false);
    }
  };

  // ── Edit ────────────────────────────────────────────────────────────────────
  const handleEditClick = () => {
    if (mission) {
      setEditForm({ name: mission.name, description: mission.description || '' });
      setIsEditModalOpen(true);
    }
  };

  const handleSaveEdit = async () => {
    if (!id) return;
    try {
      setIsSaving(true);
      setError(null);
      const response = await api.patch(`/missions/${id}`, editForm);
      setMission(response.data.data.mission);
      setIsEditModalOpen(false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update mission');
    } finally {
      setIsSaving(false);
    }
  };

  // ── Pilot Notes ───────────────────────────────────────────────────────────────
  const handleSaveNotes = async () => {
    if (!id || !mission) return;
    try {
      setIsSavingNotes(true);
      setNotesError(null);
      setNotesSuccess(null);
      const existingMeta = mission.metadata ?? {};
      const response = await api.patch(`/missions/${id}`, {
        metadata: { ...existingMeta, pilot_notes: pilotNotesText.trim() },
      });
      setMission(response.data.data.mission);
      setPilotNotesText(response.data.data.mission?.metadata?.pilot_notes ?? '');
      setNotesSuccess('Notes saved.');
    } catch (err: any) {
      setNotesError(err.response?.data?.message || 'Failed to save notes');
    } finally {
      setIsSavingNotes(false);
    }
  };

  // ── Complete ─────────────────────────────────────────────────────────────────
  const handleComplete = async () => {
    if (!id || !mission) return;
    if (!window.confirm('Mark this mission as completed? This cannot be undone.')) return;
    try {
      setIsCompleting(true);
      setError(null);
      const response = await api.post(`/missions/${id}/complete`);
      setMission(response.data.data.mission);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to complete mission');
    } finally {
      setIsCompleting(false);
    }
  };

  // ── Terminate ─────────────────────────────────────────────────────────────────
  const handleTerminate = async () => {
    if (!id) return;
    if (!terminationComment.trim()) {
      setTerminateError('A termination comment is required.');
      return;
    }
    try {
      setIsTerminating(true);
      setTerminateError(null);
      const response = await api.post(`/missions/${id}/terminate`, {
        termination_comment: terminationComment.trim(),
      });
      setMission(response.data.data.mission);
      setIsTerminateModalOpen(false);
      setTerminationComment('');
    } catch (err: any) {
      setTerminateError(err.response?.data?.message || 'Failed to terminate mission');
    } finally {
      setIsTerminating(false);
    }
  };

  // ── File upload ───────────────────────────────────────────────────────────────
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files;
    if (!selected || selected.length === 0 || !id) return;

    setIsUploading(true);
    setUploadError(null);
    setUploadSuccess(null);

    try {
      const formData = new FormData();
      Array.from(selected).forEach((f) => formData.append('images', f));

      await api.post(`/missions/${id}/images`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setUploadSuccess(`${selected.length} file${selected.length > 1 ? 's' : ''} uploaded successfully.`);
      // Reload files + stats
      const [filesRes, statsRes] = await Promise.all([
        api.get(`/missions/${id}/images`),
        api.get(`/missions/${id}/stats`),
      ]);
      setFiles(filesRes.data.data.images || []);
      setStats(statsRes.data.data.stats);
    } catch (err: any) {
      setUploadError(err.response?.data?.message || 'Upload failed. Please try again.');
    } finally {
      setIsUploading(false);
      // Reset file input so the same file can be re-selected if needed
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleOpenGisPlatform = async () => {
    if (!id) return;

    try {
      setIsOpeningGis(true);
      setError(null);
      const response = await gisPlatformApi.getLaunchUrl({
        missionId: id,
        returnUrl: window.location.href,
      });
      const launchUrl = response.data.launchUrl as string;
      window.open(launchUrl, '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to open GIS platform');
    } finally {
      setIsOpeningGis(false);
    }
  };


  // ── Helpers ───────────────────────────────────────────────────────────────────
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'in_progress': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'planned': return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
      case 'failed': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const canAct = mission && mission.status !== 'completed' && mission.status !== 'failed';

  // ── Render ────────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !mission) {
    return (
      <div className="space-y-4">
        <Alert type="error">{error || 'Mission not found'}</Alert>
        <Button onClick={() => navigate('/missions')} variant="outline">Back to Missions</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ── Header ── */}
      <div className="space-y-3">
        {/* Row 1: back + title + status badge */}
        <div className="flex items-start gap-3">
          <Button onClick={() => navigate('/missions')} variant="outline" className="flex-shrink-0">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </Button>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(mission.status)}`}>
                {mission.status.replace('_', ' ').toUpperCase()}
              </span>
              {mission.metadata?.integrity_seal && (
                <span className="inline-flex items-center px-3 py-1 text-sm font-medium rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                  <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Integrity Sealed
                </span>
              )}
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white break-words">
              {mission.name}
            </h1>
            {mission.description && (
              <p className="mt-1 text-gray-600 dark:text-gray-400 break-words">{mission.description}</p>
            )}
          </div>
        </div>

        {/* Row 2: Edit + action buttons */}
        <div className="flex flex-wrap items-center gap-3">
          <Button onClick={handleEditClick} variant="outline" size="sm">
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Edit
          </Button>
          <Button onClick={handleOpenGisPlatform} variant="outline" size="sm" disabled={isOpeningGis}>
            {isOpeningGis ? (
              <><Spinner size="sm" className="mr-2" />Opening GIS…</>
            ) : (
              <>
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 01.553-.894L9 2m0 18l6-2m-6 2V2m6 16l5.447-2.724A1 1 0 0021 14.382V3.618a1 1 0 00-.553-.894L15 0m0 18V0m-6 2l6-2" />
                </svg>
                Open in GIS Platform
              </>
            )}
          </Button>
          {canAct && (
            <>
              <Button
                onClick={handleComplete}
                variant="primary"
                disabled={isCompleting}
              >
                {isCompleting ? (
                  <><Spinner size="sm" className="mr-2" />Completing…</>
                ) : (
                  <>
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Complete Mission
                  </>
                )}
              </Button>
              <Button
                onClick={() => { setTerminateError(null); setTerminationComment(''); setIsTerminateModalOpen(true); }}
                variant="outline"
                className="border-red-300 text-red-600 hover:bg-red-50 dark:border-red-700 dark:text-red-400 dark:hover:bg-red-900/20"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Terminate
              </Button>
            </>
          )}
        </div>
      </div>

      {error && <Alert type="error" onClose={() => setError(null)}>{error}</Alert>}

      {/* ── Stats Cards ── */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button type="button" onClick={() => setActiveTab('files')} className="text-left group">
            <Card className="p-6 group-hover:ring-2 group-hover:ring-blue-400 dark:group-hover:ring-blue-500 transition-shadow cursor-pointer">
              <div className="flex items-center">
                <div className="flex-shrink-0 bg-blue-100 dark:bg-blue-900 rounded-md p-3">
                  <svg className="h-6 w-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Files</p>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white">{stats.images}</p>
                </div>
              </div>
            </Card>
          </button>
          <button type="button" onClick={() => setActiveTab('analysis')} className="text-left group">
            <Card className="p-6 group-hover:ring-2 group-hover:ring-purple-400 dark:group-hover:ring-purple-500 transition-shadow cursor-pointer">
              <div className="flex items-center">
                <div className="flex-shrink-0 bg-purple-100 dark:bg-purple-900 rounded-md p-3">
                  <svg className="h-6 w-6 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Analyses</p>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white">{stats.analyses}</p>
                </div>
              </div>
            </Card>
          </button>
          <Card className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-green-100 dark:bg-green-900 rounded-md p-3">
                <svg className="h-6 w-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Size</p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-white">{stats.totalSizeFormatted}</p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* ── Tabs ── */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8">
          {(['overview', 'files', 'analysis'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`${
                activeTab === tab
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm capitalize`}
            >
              {tab === 'files' ? `Files (${files.length})` : tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </nav>
      </div>

      {/* ── Overview Tab ── */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Mission Details */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Mission Details</h3>
              <dl className="space-y-3">
                {mission.flight_date && (
                  <div className="flex justify-between">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Flight Date</dt>
                    <dd className="text-sm text-gray-900 dark:text-white">{formatDate(mission.flight_date)}</dd>
                  </div>
                )}
                {mission.area_hectares && (
                  <div className="flex justify-between">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Area</dt>
                    <dd className="text-sm text-gray-900 dark:text-white">{mission.area_hectares} hectares</dd>
                  </div>
                )}
                {mission.crop_type && (
                  <div className="flex justify-between">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Crop Type</dt>
                    <dd className="text-sm text-gray-900 dark:text-white">{mission.crop_type}</dd>
                  </div>
                )}
                {mission.location?.coordinates && Array.isArray(mission.location.coordinates) && mission.location.coordinates.length >= 2 && (
                  <div className="flex justify-between">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Coordinates</dt>
                    <dd className="text-sm text-gray-900 dark:text-white font-mono">
                      {mission.location.coordinates[1].toFixed(6)}, {mission.location.coordinates[0].toFixed(6)}
                    </dd>
                  </div>
                )}
                <div className="flex justify-between">
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Created</dt>
                  <dd className="text-sm text-gray-900 dark:text-white">{formatDate(mission.created_at)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Last Updated</dt>
                  <dd className="text-sm text-gray-900 dark:text-white">{formatDate(mission.updated_at)}</dd>
                </div>
              </dl>
            </Card>

            {/* Hardware Metadata */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Hardware Metadata</h3>
              <dl className="space-y-3">
                {mission.drone_serial_number && (
                  <div className="flex justify-between">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Drone Serial</dt>
                    <dd className="text-sm text-gray-900 dark:text-white font-mono">{mission.drone_serial_number}</dd>
                  </div>
                )}
                {mission.sensor_calibration_date && (
                  <div className="flex justify-between">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Sensor Calibration</dt>
                    <dd className="text-sm text-gray-900 dark:text-white">{formatDate(mission.sensor_calibration_date)}</dd>
                  </div>
                )}
                {mission.pilot_id && (
                  <div className="flex justify-between">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Pilot ID</dt>
                    <dd className="text-sm text-gray-900 dark:text-white font-mono">{mission.pilot_id}</dd>
                  </div>
                )}
                {mission.telemetry_correlation_status && (
                  <div className="flex justify-between">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Telemetry Status</dt>
                    <dd className="text-sm">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        mission.telemetry_correlation_status === 'correlated'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : mission.telemetry_correlation_status === 'failed'
                          ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                          : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                      }`}>
                        {mission.telemetry_correlation_status.toUpperCase()}
                      </span>
                    </dd>
                  </div>
                )}
                {mission.metadata?.ingestion_timestamp && (
                  <div className="flex justify-between">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Ingestion Time</dt>
                    <dd className="text-sm text-gray-900 dark:text-white">{formatDate(mission.metadata.ingestion_timestamp)}</dd>
                  </div>
                )}
              </dl>
            </Card>
          </div>

          {/* Observations / Notes — always shown so pilot can add notes at any time */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Observations &amp; Notes</h3>
            <div className="space-y-4">

              {/* Editable pilot notes */}
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
                  Pilot Notes
                </label>
                <textarea
                  value={pilotNotesText}
                  onChange={(e) => { setPilotNotesText(e.target.value); setNotesSuccess(null); setNotesError(null); }}
                  rows={4}
                  placeholder="Record observations, anomalies noticed, equipment performance, weather conditions…"
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:text-white"
                />
                {notesError && <p className="mt-1 text-xs text-red-500">{notesError}</p>}
                {notesSuccess && <p className="mt-1 text-xs text-green-600 dark:text-green-400">{notesSuccess}</p>}
                <div className="flex justify-end mt-2">
                  <Button
                    onClick={handleSaveNotes}
                    variant="outline"
                    size="sm"
                    disabled={isSavingNotes}
                  >
                    {isSavingNotes ? (
                      <><Spinner size="sm" className="mr-1" />Saving…</>
                    ) : (
                      <>
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                        </svg>
                        Save Notes
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Termination info (read-only) */}
              {mission.metadata?.termination_comment && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-red-500 dark:text-red-400 mb-1">Termination Reason</p>
                  <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3">
                    {mission.metadata.termination_comment}
                  </p>
                  {mission.metadata.terminated_at && (
                    <p className="mt-1 text-xs text-gray-400">
                      Terminated {formatDate(mission.metadata.terminated_at)}
                      {mission.metadata.terminated_by ? ` by ${mission.metadata.terminated_by}` : ''}
                    </p>
                  )}
                </div>
              )}

              {/* Completion info (read-only) */}
              {mission.metadata?.completed_at && !mission.metadata?.termination_comment && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-green-600 dark:text-green-400 mb-1">Completed</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {formatDate(mission.metadata.completed_at)}
                    {mission.metadata.completed_by ? ` by ${mission.metadata.completed_by}` : ''}
                  </p>
                </div>
              )}
            </div>
          </Card>
        </div>
      )}

      {/* ── Files Tab ── */}
      {activeTab === 'files' && (
        <Card className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Uploaded Files</h3>
            {/* Hidden native file input */}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              accept="image/*,.tif,.tiff,.las,.laz,.log,.csv,.gpx,.geojson,.kml,.kmz,.zip"
              onChange={handleFileChange}
            />
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setShowUploadGuidance(v => !v)}
                className={`p-1.5 rounded-lg transition-colors ${
                  showUploadGuidance
                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-800/50 dark:text-blue-300'
                    : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30'
                }`}
                aria-label={showUploadGuidance ? 'Hide upload guidance' : 'Show upload guidance'}
                title="What files to upload and when"
              >
                <Info className="w-4 h-4" />
              </button>
              <Button
                onClick={() => fileInputRef.current?.click()}
                variant="primary"
                size="sm"
                disabled={isUploading}
              >
                {isUploading ? (
                  <><Spinner size="sm" className="mr-2" />Uploading…</>
                ) : (
                  <>
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    Upload Files
                  </>
                )}
              </Button>
            </div>
          </div>

          {showUploadGuidance && <UploadGuidancePanel onClose={() => setShowUploadGuidance(false)} />}

          {uploadError && <Alert type="error" onClose={() => setUploadError(null)} className="mb-4">{uploadError}</Alert>}
          {uploadSuccess && <Alert type="success" onClose={() => setUploadSuccess(null)} className="mb-4">{uploadSuccess}</Alert>}

          {files.length === 0 ? (
            <div className="text-center py-12">
              <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <h3 className="mt-4 text-sm font-medium text-gray-900 dark:text-white">No files uploaded</h3>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                Upload drone imagery, LiDAR data, telemetry logs, or flight records
              </p>
              <div className="mt-6">
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  variant="primary"
                  disabled={isUploading}
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  Upload Your First File
                </Button>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">File Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Size</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Integrity</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Uploaded</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                  {files.map((file) => (
                    <tr key={file.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                        {file.metadata?.file_name || file.file_path.split('/').pop()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <FileTypeBadge dataType={file.data_type || file.metadata?.data_type || 'image'} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {formatFileSize(file.file_size)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${
                          file.metadata?.upload_status === 'completed'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            : file.metadata?.upload_status === 'failed'
                            ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                            : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                        }`}>
                          {file.metadata?.upload_status === 'completed'
                            ? <><CheckCircle2 className="w-3 h-3" /> Completed</>
                            : file.metadata?.upload_status === 'failed'
                            ? <><AlertCircle className="w-3 h-3" /> Failed</>
                            : (file.metadata?.upload_status || 'Pending').toUpperCase()}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-400 dark:text-gray-500 font-mono">
                        {file.metadata?.sha256
                          ? <span title={file.metadata.sha256} className="text-green-600 dark:text-green-400">✓ {file.metadata.sha256.slice(7, 19)}…</span>
                          : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {formatDate(file.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* ── Analysis Tab ── */}
      {activeTab === 'analysis' && (
        <div className="space-y-4">
          {analysis.length === 0 ? (
            <Card className="p-6">
              <div className="text-center py-8">
                <Activity className="mx-auto h-12 w-12 text-gray-300 dark:text-gray-600" />
                <p className="mt-3 text-sm font-medium text-gray-500 dark:text-gray-400">No analysis results yet</p>
                <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                  Upload flight data via the Files tab to trigger the AI analysis pipeline.
                </p>
              </div>
            </Card>
          ) : (
            analysis.map((a) => (
              <Card key={a.id} className="p-6">
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-semibold px-2 py-1 rounded bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300">
                      {ANALYSIS_TYPE_LABEL[a.analysis_type] ?? a.analysis_type}
                    </span>
                    {a.metadata?.severity_level && (
                      <span className={`text-xs font-semibold px-2 py-1 rounded ${
                        a.metadata.severity_level === 'high' || a.metadata.severity_level === 'critical'
                          ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                          : a.metadata.severity_level === 'medium'
                          ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300'
                          : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300'
                      }`}>
                        {a.metadata.severity_level.toUpperCase()}
                      </span>
                    )}
                    {a.metadata?.satellite_drone_match === true && (
                      <span className="text-xs font-semibold px-2 py-1 rounded bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                        ✓ Satellite–Drone Match
                      </span>
                    )}
                  </div>
                  <div className="text-right flex-shrink-0">
                    {a.confidence != null && (
                      <p className="text-sm font-bold text-gray-900 dark:text-white">
                        {Math.round(a.confidence * 100)}%
                        <span className="text-xs font-normal text-gray-400 ml-1">confidence</span>
                      </p>
                    )}
                    <p className="text-xs text-gray-400 mt-0.5">{formatDate(a.created_at)}</p>
                  </div>
                </div>

                {/* Diagnosis / key metrics */}
                {(a.metadata?.issue_classification || a.metadata?.affected_area_ha) && (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                    {a.metadata.issue_classification && (
                      <div className="col-span-2 rounded-lg bg-gray-50 dark:bg-gray-800 p-3">
                        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-0.5">Diagnosis</p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white leading-snug">{a.metadata.issue_classification}</p>
                      </div>
                    )}
                    {a.metadata.affected_area_ha != null && (
                      <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-3">
                        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-0.5">Affected Area</p>
                        <p className="text-sm font-bold text-gray-900 dark:text-white">{a.metadata.affected_area_ha} ha</p>
                      </div>
                    )}
                    {a.metadata.ndvi_affected != null && (
                      <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-3">
                        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-0.5">NDVI (affected)</p>
                        <p className="text-sm font-bold text-red-600 dark:text-red-400">{a.metadata.ndvi_affected.toFixed(2)}</p>
                        {a.metadata.ndvi_healthy != null && (
                          <p className="text-[10px] text-gray-400">healthy: {a.metadata.ndvi_healthy.toFixed(2)}</p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Narrative */}
                {a.metadata?.narrative && (
                  <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-4">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">AI Narrative</p>
                    <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">{a.metadata.narrative}</p>
                  </div>
                )}

                {/* Recommended actions */}
                {Array.isArray(a.metadata?.recommended_actions) && a.metadata!.recommended_actions.length > 0 && (
                  <div className="mt-4">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Recommended Actions</p>
                    <ol className="space-y-2">
                      {a.metadata!.recommended_actions.map((action: any, i: number) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                          <span className="flex-shrink-0 w-5 h-5 rounded-full bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300 text-xs font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
                          <span>
                            <strong>{action.type?.replace(/_/g, ' ')}</strong>
                            {action.urgency && <span className="ml-1 text-xs text-orange-500">({action.urgency.replace(/_/g, ' ')})</span>}
                            {action.chemical && <span className="ml-1">— {action.chemical}</span>}
                            {action.description && <span className="ml-1">— {action.description}</span>}
                          </span>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}

                {a.metadata?.model_used && (
                  <p className="mt-3 text-[10px] text-gray-400">Model: {a.metadata.model_used}</p>
                )}
              </Card>
            ))
          )}
        </div>
      )}

      {/* ── Edit Mission Modal ── */}
      {isEditModalOpen && (
        <Modal onClose={() => setIsEditModalOpen(false)} raw>
          <Card className="w-full max-w-lg">
            <div className="p-6 overflow-y-auto max-h-[calc(100dvh-2rem)]">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Edit Mission</h2>
                <button onClick={() => setIsEditModalOpen(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Mission Name *</label>
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:text-white"
                    placeholder="Enter mission name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                  <textarea
                    value={editForm.description}
                    onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:text-white"
                    placeholder="Enter mission description"
                  />
                </div>
                {error && <Alert type="error">{error}</Alert>}
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <Button onClick={() => setIsEditModalOpen(false)} variant="outline" disabled={isSaving}>Cancel</Button>
                <Button onClick={handleSaveEdit} variant="primary" disabled={isSaving || !editForm.name.trim()}>
                  {isSaving ? <><Spinner size="sm" className="mr-2" />Saving…</> : 'Save Changes'}
                </Button>
              </div>
            </div>
          </Card>
        </Modal>
      )}

      {/* ── Terminate Mission Modal ── */}
      {isTerminateModalOpen && (
        <Modal onClose={() => setIsTerminateModalOpen(false)} raw>
          <Card className="w-full max-w-lg">
            <div className="p-6 overflow-y-auto max-h-[calc(100dvh-2rem)]">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-red-600 dark:text-red-400">Terminate Mission</h2>
                <button
                  onClick={() => setIsTerminateModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  disabled={isTerminating}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                This will mark the mission as <strong>failed</strong> and record why it was stopped.
                This action cannot be undone.
              </p>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Termination Reason <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={terminationComment}
                  onChange={(e) => { setTerminationComment(e.target.value); setTerminateError(null); }}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-red-500 focus:border-red-500 dark:bg-gray-800 dark:text-white"
                  placeholder="Describe why the mission is being terminated (e.g. mechanical failure, safety concern, weather)…"
                />
              </div>

              {terminateError && <Alert type="error" className="mt-3">{terminateError}</Alert>}

              <div className="flex justify-end gap-3 mt-6">
                <Button
                  onClick={() => setIsTerminateModalOpen(false)}
                  variant="outline"
                  disabled={isTerminating}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleTerminate}
                  disabled={isTerminating || !terminationComment.trim()}
                  className="bg-red-600 hover:bg-red-700 text-white border-red-600 hover:border-red-700 focus:ring-red-500"
                >
                  {isTerminating ? (
                    <><Spinner size="sm" className="mr-2" />Terminating…</>
                  ) : (
                    'Terminate Mission'
                  )}
                </Button>
              </div>
            </div>
          </Card>
        </Modal>
      )}
    </div>
  );
};
